/* eslint-disable react-hooks/exhaustive-deps -- Тут во всех эффектах проблемы. Их нужно исправлять скопом. MOSCAT-309 */
import {
  ChangeEvent,
  forwardRef,
  MouseEventHandler,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { VisuallyHidden } from 'reakit';
import { Box } from '../Box/Box';
import CustomEvent from '../utils/createCustomEvent';
import generateId from '../utils/generateId';
import { InputFile } from './inner/InputFile';
import { FilesForm } from './inner/FilesForm/FilesForm';
import InnerMessage from './inner/InnerMessage';
import {
  FileUploaderMessage,
  FileUploaderMessageType,
} from './inner/FileUploaderMessage';
import { CodeTypes, FileNotificationStates, FileStates } from './common';
import {
  fileFormatValidatorCreator,
  getBytes,
  getNotificationText,
  innerErrors,
} from './helpers';
import { useFileUploaderRequest } from './useFileUploaderRequest';
import { FetchPropsType, FileUploaderProps, FileType } from './types';

export { CodeTypes as FileCodeTypes, FileStates } from './common';
export type { FileType, FetchPropsType };
export { FileUploaderMessageType };

/**
 * @beta
 */
export const FileUploader = forwardRef(
  (props: FileUploaderProps, ref: Ref<HTMLInputElement>): JSX.Element => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
    const [formErrorId, setFormErrorId] = useState<string>();
    const [fileForUpdate, setFileForUpdate] = useState<FileType>();
    const [limitWarning, setLimitWarning] = useState<number>(0);
    const [limitError, setLimitError] = useState<number>(0);
    const [notification, setNotification] = useState<string>('');
    const [lastFileNotification, setLastFileNotification] = useState<{
      fileName: string;
      type: FileNotificationStates;
    } | null>(null);

    const {
      maxFileSize,
      maxFiles,
      formats,
      formError,
      fetchParams,
      options,
      files,
      onChange: onFilesChange,
      name,
      styles,
      ...inputProps
    } = props;

    const layoutAcceptFormats = formats?.join(', ').toUpperCase();
    const loadedFilesCount = files.filter(
      ({ code }) => code === CodeTypes.SUCCESS
    ).length;

    const request = useFileUploaderRequest(props);

    const hint =
      maxFiles === 1
        ? `Вы\u00A0можете загрузить\u00A0${maxFiles} файл ${layoutAcceptFormats}, размер\u00A0\u2014 до\u00A0${
            maxFileSize / 1024
          }\u00A0МБ.`
        : `Вы\u00A0можете загрузить до\u00A0${maxFiles} файлов ${layoutAcceptFormats}, размер одного\u00A0\u2014 до\u00A0${
            maxFileSize / 1024
          }\u00A0МБ.`;

    const onCustomFilesChange = (changedFiles: FileType[]) => {
      let event = CustomEvent('filesChange', {
        bubbles: true,
        cancelable: true,
        detail: { name, files: changedFiles },
      }) as CustomEvent<{ name?: string; files: FileType[] }>;
      onFilesChange(event);
    };

    const setFileUploaderNotification = (newNotification: string) => {
      setNotification(newNotification);
      setTimeout(() => {
        setLastFileNotification(null);
        setNotification('');
      }, 2000);
    };

    useEffect(
      () => () => {
        const filesCopy = [...files];
        // eslint-disable-next-line @typescript-eslint/prefer-for-of -- требуется рефакторинг
        for (let i = 0; i < filesCopy.length; i++) {
          const currentFile = filesCopy[i];
          if (
            currentFile.controller &&
            currentFile.state === FileStates.loading
          ) {
            const currentIndex = filesCopy.findIndex(
              (file) => file.id === currentFile.id
            );
            filesCopy.splice(currentIndex, 1);
            currentFile.controller.abort();
          }
        }
        if (filesCopy.length !== files.length) onCustomFilesChange(filesCopy);
      },
      []
    );

    useEffect(() => {
      setFormErrorId(generateId());
    }, [formError]);

    useEffect(() => {
      if (limitWarning) setLimitWarning(0);
    }, [files.length]);

    useEffect(() => {
      const hasLoadingFiles = files.some(
        ({ state }) => state === FileStates.loading
      );

      if (lastFileNotification?.type === FileNotificationStates.delete) {
        setFileUploaderNotification(
          getNotificationText.deleteText(lastFileNotification.fileName)
        );
        return;
      }

      if (lastFileNotification?.type === FileNotificationStates.cancelUpload) {
        setFileUploaderNotification(
          getNotificationText.cancelUploadText(lastFileNotification.fileName)
        );
        return;
      }

      if (lastFileNotification?.type === FileNotificationStates.tryLoadAgain) {
        setFileUploaderNotification(
          getNotificationText.tryAgainText(lastFileNotification.fileName)
        );
        return;
      }

      if (inputProps.multiple && files.length && !hasLoadingFiles) {
        setFileUploaderNotification(
          getNotificationText.commonInfo(loadedFilesCount, files.length)
        );
      }
    }, [files, lastFileNotification, inputProps.multiple]);

    useEffect(() => {
      const filesCopy = [...files];
      if (fileForUpdate) {
        const currentFileIndex = filesCopy.findIndex(
          (file) => file.id === fileForUpdate.id
        );
        filesCopy.splice(currentFileIndex, 1, fileForUpdate);
        onCustomFilesChange(filesCopy);
      }
    }, [fileForUpdate]);

    const fileFormatValidator = useCallback(
      fileFormatValidatorCreator(formats),
      [formats]
    );
    const fetchFileWrapper = (
      file: File,
      id: string,
      controller: AbortController
    ) => {
      request({ file, controller, restProps: { options, fetchParams } })
        .then((res) => res.json())
        .then(
          (
            response:
              | { code: CodeTypes.SUCCESS; data: { document_id: string } }
              | { code: Exclude<CodeTypes, CodeTypes.SUCCESS>; data: undefined }
          ) => {
            let { document_id: documentId } = response.data ?? {
              document_id: undefined,
            };
            const { code } = response;
            const state =
              code === CodeTypes.SUCCESS
                ? FileStates.success
                : FileStates.error;
            setFileForUpdate({ id, documentId, state, code, file, controller });
          }
        )
        .catch(() => {
          if (!controller.signal.aborted) {
            setFileForUpdate({
              id,
              state: FileStates.error,
              code: CodeTypes.ERROR_INTERNAL,
              file,
              controller,
            });
          }
        });
    };
    const handleFilesClick: MouseEventHandler<HTMLInputElement> = (event) => {
      if (files.length >= maxFiles) {
        event.preventDefault();
        setLimitWarning(limitWarning + 1);
      }
    };
    const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
      setTimeout(() => inputRef.current?.focus());
      const targetFiles = event.target.files;
      if (!targetFiles) return;
      setLimitError(0);
      const filesKeys = Object.getOwnPropertyNames(targetFiles).filter(
        (prop) => prop !== 'length'
      );
      setFileUploaderNotification(
        getNotificationText.commonLoadingInfo(filesKeys.length)
      );
      if (filesKeys.length > maxFiles - files.length) {
        setLimitError(limitError + 1);
        return;
      }
      let formattedTargetFiles: FileType[] = [];
      filesKeys.forEach((fileKey) => {
        let code: CodeTypes = CodeTypes.LOADING;
        const file = targetFiles[Number(fileKey)];
        const fileId = generateId();
        if (fileFormatValidator(file.name)) {
          code = CodeTypes.VALIDATION_FAILED;
        } else if (file.size > getBytes(maxFileSize)) {
          code = CodeTypes.SIZE_EXCEEDED;
        }
        const currentState =
          code === CodeTypes.LOADING ? FileStates.loading : FileStates.error;
        let newFile: FileType = { id: fileId, state: currentState, file, code };
        if (code === CodeTypes.LOADING) {
          const controller = new AbortController();
          newFile.controller = controller;
          fetchFileWrapper(file, fileId, controller);
        }
        formattedTargetFiles.push(newFile);
      });
      onCustomFilesChange([...files, ...formattedTargetFiles]);
    };
    const handleFileDelete = (id: string) => {
      const filesCopy = [...files];
      const currentFileIndex = filesCopy.findIndex((file) => file.id === id);
      setLastFileNotification({
        fileName: filesCopy[currentFileIndex].file.name,
        type: FileNotificationStates.delete,
      });
      filesCopy.splice(currentFileIndex, 1);
      onCustomFilesChange(filesCopy);
      setLimitError(0);
    };
    const handleCancelFileUpload = (id: string) => {
      const filesCopy = [...files];
      const currentFile = filesCopy.find((file) => file.id === id) as FileType;
      if (currentFile.controller) {
        currentFile.controller.abort();
        handleFileDelete(id);
        setLastFileNotification({
          fileName: currentFile.file.name,
          type: FileNotificationStates.cancelUpload,
        });
      }
    };
    const handleTryAgain = (id: string) => {
      const filesCopy = [...files];
      const controller = new AbortController();
      const currentFile = filesCopy.find((file) => file.id === id) as FileType;
      filesCopy.splice(filesCopy.indexOf(currentFile), 1, {
        ...currentFile,
        state: FileStates.loading,
        controller,
      });
      onCustomFilesChange(filesCopy);
      setLastFileNotification({
        fileName: currentFile.file.name,
        type: FileNotificationStates.tryLoadAgain,
      });
      fetchFileWrapper(currentFile.file as File, id, controller);
    };

    return (
      <Box maxWidth={[1, 'fileUploaderMaxWidth']} styles={styles}>
        <VisuallyHidden>{hint}</VisuallyHidden>
        {formError && formErrorId && (
          <FileUploaderMessage formError={formError} id={formErrorId} />
        )}
        <FilesForm
          inputRef={inputRef}
          files={files}
          onDeleteFile={handleFileDelete}
          onTryAgain={handleTryAgain}
          onCancel={handleCancelFileUpload}
        />
        <InnerMessage
          kind="warning"
          visibilityCondition={!limitError && limitWarning}
          message={innerErrors.LIMIT}
        />
        <InnerMessage
          kind="error"
          visibilityCondition={limitError}
          message={innerErrors.LIMIT_EXCEEDED}
        />
        <InputFile
          formats={formats}
          formErrorId={formErrorId}
          inputProps={inputProps}
          ref={inputRef}
          onClick={handleFilesClick}
          onChange={handleFilesChange}
        />
        <Box mt="sp-12" color="gray-60" aria-hidden="true">
          {hint}
        </Box>
        <VisuallyHidden key={notification} role="alert" aria-live="assertive">
          {notification}
        </VisuallyHidden>
      </Box>
    );
  }
);
/* eslint-enable react-hooks/exhaustive-deps -- Включаем обратно */
